export function createFocusTrap({
                                    containers = [],
                                    initialFocus = null,
                                    onEscape = null,
                                    returnFocus = true
                                } = {}) {
    const focusableSelector = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(", ");

    let active = false;
    let lastFocused = null;

    const getContainers = () =>
        containers.filter((el) => el instanceof HTMLElement);

    const isVisible = (el) => {
        if (!(el instanceof HTMLElement)) return false;
        if (el.hidden) return false;
        if (el.closest("[hidden]")) return false;
        if (el.closest("[inert]")) return false;

        const style = window.getComputedStyle(el);
        if (style.display === "none") return false;
        if (style.visibility === "hidden") return false;

        return el.getClientRects().length > 0;
    };

    const getFocusable = () => {
        const seen = new Set();

        return getContainers()
            .flatMap((container) => Array.from(container.querySelectorAll(focusableSelector)))
            .filter((el) => {
                if (!(el instanceof HTMLElement)) return false;
                if (!isVisible(el)) return false;
                if (seen.has(el)) return false;

                seen.add(el);
                return true;
            });
    };

    const onKeydown = (e) => {
        if (!active) return;

        if (e.key === "Escape") {
            if (typeof onEscape === "function") {
                e.preventDefault();
                onEscape();
            }
            return;
        }

        if (e.key !== "Tab") return;

        const focusables = getFocusable();
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const activeElement = document.activeElement;

        if (!e.shiftKey && activeElement === last) {
            e.preventDefault();
            first.focus({ preventScroll: true });
            return;
        }

        if (e.shiftKey && activeElement === first) {
            e.preventDefault();
            last.focus({ preventScroll: true });
            return;
        }

        if (activeElement instanceof Node && !focusables.includes(activeElement)) {
            e.preventDefault();
            (e.shiftKey ? last : first).focus({ preventScroll: true });
        }
    };

    const activate = () => {
        if (active) return;

        active = true;
        lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        document.addEventListener("keydown", onKeydown, true);

        requestAnimationFrame(() => {
            if (initialFocus instanceof HTMLElement) {
                initialFocus.focus({ preventScroll: true });
                return;
            }

            getFocusable()[0]?.focus({ preventScroll: true });
        });
    };

    const deactivate = () => {
        if (!active) return;

        active = false;
        document.removeEventListener("keydown", onKeydown, true);

        if (returnFocus && lastFocused instanceof HTMLElement) {
            lastFocused.focus({ preventScroll: true });
        }
    };

    return {
        activate,
        deactivate,
        isActive: () => active
    };
}